<template>
  <div :class="['toast', `toast-${variant}`, { dismissing, pulse }]"
       @mouseenter="pauseProgress" @mouseleave="resumeProgress">
    <div class="toast-icon">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" v-html="icon" />
    </div>
    <div class="toast-content">
      <div class="toast-title">{{ displayMessage }}</div>
    </div>
    <button class="toast-close" @click="dismiss">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
    <div v-if="!persist" class="toast-progress" ref="progressBar" />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';

const ICONS = {
  success: '<polyline points="20 6 9 17 4 12"/>',
  error:   '<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>',
  warning: '<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
  info:    '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
};

const DURATION = 4000;

export default {
  props: {
    message: { type: String, required: true },
    status:  { type: String, default: 'success' },
    id:      { type: String, required: true },
    count:   { type: Number, default: 1 },
    // 2026-10-18: a payroll-correctness message persists until dismissed. See
    // notificationStore's addNotification for why this is not a preference.
    persist: { type: Boolean, default: false },
  },
  emits: ['closed'],
  setup(props, { emit }) {
    const dismissing = ref(false);
    const pulse = ref(false);
    const progressBar = ref(null);
    let timer = null;

    const variant = computed(() => {
      const s = props.status;
      if (s === 'error' || s === 'warning' || s === 'info') return s;
      return 'success';
    });

    const icon = computed(() => ICONS[variant.value]);

    const displayMessage = computed(() => (
      props.count > 1 ? `${props.count}x ${props.message}` : props.message
    ));

    const dismiss = () => {
      if (dismissing.value) return;
      dismissing.value = true;
      clearTimeout(timer);
      setTimeout(() => emit('closed', props.id), 200);
    };

    const scheduleClose = (delay = DURATION) => {
      timer = setTimeout(dismiss, delay);
    };

    const pauseProgress = () => {
      clearTimeout(timer);
      if (progressBar.value) progressBar.value.style.animationPlayState = 'paused';
    };

    const resumeProgress = () => {
      // ⚠ AND MOVING THE MOUSE OFF A PERSISTENT TOAST MUST NOT START A 1s
      // TIMER. This is the branch that would quietly undo the whole change:
      // onMounted skips the timer, and then the first hover schedules one.
      if (props.persist) return;
      if (progressBar.value) progressBar.value.style.animationPlayState = 'running';
      scheduleClose(1000);
    };

    // A repeat of the same message bumps `count` (see notificationStore) rather
    // than mounting a new toast — restart the auto-dismiss clock and give a
    // brief pulse so the incremented count is actually noticeable.
    watch(() => props.count, () => {
      clearTimeout(timer);
      if (!props.persist) scheduleClose();
      pulse.value = true;
      setTimeout(() => { pulse.value = false; }, 300);
    });

    onMounted(() => {
      // ⚠ A PERSISTENT TOAST SCHEDULES NOTHING. Not a longer timer — none.
      // The warning arrives after the write, so there is no second chance to
      // show it, and any finite timer is a guess about how long somebody is
      // looking at the screen.
      if (!props.persist) scheduleClose();
    });

    onBeforeUnmount(() => {
      clearTimeout(timer);
    });

    return { dismissing, pulse, progressBar, variant, icon, displayMessage, dismiss, pauseProgress, resumeProgress };
  }
};
</script>

<style scoped>
.toast {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  min-width: 280px;
  max-width: 380px;
  box-shadow: 0 4px 16px rgba(0,0,0,.1), 0 1px 4px rgba(0,0,0,.06);
  border: 1px solid;
  animation: toastIn .22s cubic-bezier(.34,1.3,.64,1);
  position: relative;
  overflow: hidden;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

.toast.dismissing {
  animation: toastOut .2s ease forwards;
}

@keyframes toastIn {
  from { opacity: 0; transform: translateX(20px) scale(.97); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
}

@keyframes toastOut {
  from { opacity: 1; transform: translateX(0) scale(1); }
  to   { opacity: 0; transform: translateX(12px) scale(.97); }
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  border-radius: 0 0 0 12px;
  animation: progress 4s linear forwards;
}

@keyframes progress {
  from { width: 100%; }
  to   { width: 0%; }
}

.toast-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.toast-icon svg {
  width: 14px;
  height: 14px;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}

.toast.pulse .toast-title {
  animation: toastPulse .3s ease;
}

@keyframes toastPulse {
  0%   { transform: scale(1); }
  30%  { transform: scale(1.08); }
  100% { transform: scale(1); }
}

.toast-close {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
  transition: background .1s;
  padding: 0;
}

.toast-close svg {
  width: 11px;
  height: 11px;
}

/* SUCCESS */
.toast-success { background: #f0fdf4; border-color: #bbf7d0; }
.toast-success .toast-icon { background: #dcfce7; }
.toast-success .toast-icon svg { color: #16a34a; }
.toast-success .toast-title { color: #14532d; }
.toast-success .toast-close { color: #16a34a; }
.toast-success .toast-close:hover { background: #dcfce7; }
.toast-success .toast-progress { background: #22c55e; }

/* ERROR */
.toast-error { background: #fff5f5; border-color: #fca5a5; }
.toast-error .toast-icon { background: #fee2e2; }
.toast-error .toast-icon svg { color: #dc2626; }
.toast-error .toast-title { color: #7f1d1d; }
.toast-error .toast-close { color: #dc2626; }
.toast-error .toast-close:hover { background: #fee2e2; }
.toast-error .toast-progress { background: #dc2626; }

/* WARNING */
.toast-warning { background: #fffbeb; border-color: #fde68a; }
.toast-warning .toast-icon { background: #fef3c7; }
.toast-warning .toast-icon svg { color: #d97706; }
.toast-warning .toast-title { color: #78350f; }
.toast-warning .toast-close { color: #d97706; }
.toast-warning .toast-close:hover { background: #fef3c7; }
.toast-warning .toast-progress { background: #f59e0b; }

/* INFO */
.toast-info { background: #eff6ff; border-color: #93c5fd; }
.toast-info .toast-icon { background: #dbeafe; }
.toast-info .toast-icon svg { color: #1e40af; }
.toast-info .toast-title { color: #1e3a5f; }
.toast-info .toast-close { color: #1e40af; }
.toast-info .toast-close:hover { background: #dbeafe; }
.toast-info .toast-progress { background: #3b82f6; }
</style>
